import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, HealthMetric, WellnessRecommendation } from '../types'
import { Vendor, Meal, DietaryPreferences } from '../services/mealmateService'

interface AppState {
  // User state
  user: User | null
  isAuthenticated: boolean
  
  // Health data
  healthMetrics: HealthMetric[]
  recommendations: WellnessRecommendation[]
  
  // Mealmate state
  selectedVendors: Vendor[]
  cartItems: { meal: Meal; quantity: number }[]
  dietaryPreferences: DietaryPreferences
  favoriteMeals: Meal[]
  favoriteVendors: Vendor[]
  
  // UI state
  isLoading: boolean
  error: string | null
  theme: 'light' | 'dark'
  
  // Actions
  setUser: (user: User | null) => void
  setAuthenticated: (authenticated: boolean) => void
  setHealthMetrics: (metrics: HealthMetric[]) => void
  addHealthMetric: (metric: HealthMetric) => void
  setRecommendations: (recommendations: WellnessRecommendation[]) => void
  
  // Mealmate actions
  addToCart: (meal: Meal, quantity: number) => void
  removeFromCart: (mealId: string) => void
  updateCartQuantity: (mealId: string, quantity: number) => void
  clearCart: () => void
  setDietaryPreferences: (preferences: DietaryPreferences) => void
  addFavoriteMeal: (meal: Meal) => void
  removeFavoriteMeal: (mealId: string) => void
  addFavoriteVendor: (vendor: Vendor) => void
  removeFavoriteVendor: (vendorId: string) => void
  
  // UI actions
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setTheme: (theme: 'light' | 'dark') => void
  clearError: () => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      healthMetrics: [],
      recommendations: [],
      selectedVendors: [],
      cartItems: [],
      dietaryPreferences: {
        restrictions: [],
        allergies: [],
        preferences: []
      },
      favoriteMeals: [],
      favoriteVendors: [],
      isLoading: false,
      error: null,
      theme: 'light',
      
      setUser: (_user) => set({ user: _user }),
      setAuthenticated: (_isAuthenticated) => set({ isAuthenticated: _isAuthenticated }),
      setHealthMetrics: (_healthMetrics) => set({ healthMetrics: _healthMetrics }),
      addHealthMetric: (_metric) => set((state) => ({ 
        healthMetrics: [...state.healthMetrics, _metric] 
      })),
      setRecommendations: (_recommendations) => set({ recommendations: _recommendations }),
      
      // Mealmate actions
      addToCart: (meal, quantity) => set((state) => {
        const existingItem = state.cartItems.find(item => item.meal.id === meal.id)
        if (existingItem) {
          return {
            cartItems: state.cartItems.map(item => 
              item.meal.id === meal.id 
                ? { ...item, quantity: item.quantity + quantity }
                : item
            )
          }
        }
        return {
          cartItems: [...state.cartItems, { meal, quantity }]
        }
      }),
      
      removeFromCart: (mealId) => set((state) => ({
        cartItems: state.cartItems.filter(item => item.meal.id !== mealId)
      })),
      
      updateCartQuantity: (mealId, quantity) => set((state) => {
        if (quantity <= 0) {
          return {
            cartItems: state.cartItems.filter(item => item.meal.id !== mealId)
          }
        }
        return {
          cartItems: state.cartItems.map(item => 
            item.meal.id === mealId 
              ? { ...item, quantity }
              : item
          )
        }
      }),
      
      clearCart: () => set({ cartItems: [] }),
      
      setDietaryPreferences: (preferences) => set({ dietaryPreferences: preferences }),
      
      addFavoriteMeal: (meal) => set((state) => ({
        favoriteMeals: [...state.favoriteMeals, meal]
      })),
      
      removeFavoriteMeal: (mealId) => set((state) => ({
        favoriteMeals: state.favoriteMeals.filter(meal => meal.id !== mealId)
      })),
      
      addFavoriteVendor: (vendor) => set((state) => ({
        favoriteVendors: [...state.favoriteVendors, vendor]
      })),
      
      removeFavoriteVendor: (vendorId) => set((state) => ({
        favoriteVendors: state.favoriteVendors.filter(vendor => vendor.id !== vendorId)
      })),
      
      // UI actions
      setLoading: (_isLoading) => set({ isLoading: _isLoading }),
      setError: (_error) => set({ error: _error }),
      setTheme: (_theme) => set({ theme: _theme }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'biotune-app-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        healthMetrics: state.healthMetrics,
        recommendations: state.recommendations,
        dietaryPreferences: state.dietaryPreferences,
        favoriteMeals: state.favoriteMeals,
        favoriteVendors: state.favoriteVendors,
        theme: state.theme,
        cartItems: state.cartItems
      })
    }
  )
)